import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar";
import { toast } from "react-toastify";
import { createUser, getAdminUsers, updateUser } from "../../utils/actions/allactions";
import { validateAdmin } from "../../utils/helper";
import useDocumentTitle from "../../utils/useDocumentTitle";
import { useInstance } from "../../context/instance.context";
import GenericLoaderComponent from "../Loading/GenericLoaderComponent";
import Toggle from "react-toggle";
import { Button, Modal } from "react-bootstrap";
import "react-toggle/style.css";
import "bootstrap/dist/css/bootstrap.min.css";

const AdminUsers = () => {
    const navigate = useNavigate();
    useDocumentTitle("Admin Users");
    const token = localStorage.getItem("Token");

    const [adminUsers, setAdminUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const companyDomain = process.env.REACT_APP_SUPPORT_EMAIL?.split("@")[1] || ""


    const [adminValue, setAdminValue] = useState(getInitialAdminValues());



    useEffect(() => {
        validateAdmin(token, navigate);
        fetchAdminUsers();
    }, []);

    const fetchAdminUsers = async () => {
        try {
            setLoading(true);
            const adminUserData = await getAdminUsers(token);
            setAdminUsers(adminUserData);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = () => {
        setAdminValue(getInitialAdminValues());
        setIsEditing(false);
        setShowModal(true);
        setError("");
    };

    const handleEditAdminUser = (adminUser) => {
        setAdminValue(adminUser);
        setIsEditing(true);
        setShowModal(true);
        setError("");
    };

    const updateEmailSubscription = (value, fieldName) => {
        setAdminValue({ ...adminValue, [fieldName]: value });
    };

    const handleSaveAdminUser = async () => {
        setError("");

        if (!adminValue.firstName) return setError("First name is required.");
        if (!adminValue.lastName) return setError("Last name is required.");
        if (!adminValue.email) return setError("Email is required.");
        if (!adminValue.accountStatus)
            return setError("Account status is required.");

        const data = { ...adminValue };
        if (!data.email.includes("@")) {
            data.email = `${data.email}@${companyDomain}`;
        }
        setShowModal(false);


        try {
            setLoading(true)
            if (data?._id) {
                try {
                    //check if email already exsits
                    const emailExists = adminUsers.find((user) => user.email === data.email && user._id !== data._id);
                    if (emailExists) {
                        toast.error("Admin user already exists");
                        return;
                    }
                    const createdUser = await updateUser(token, data);

                    if (createdUser.data.message === "Error creating a user") {
                        toast.error('Error creating a user');
                    } else {
                        toast.success("Admin user updated successfully");
                    }
                } catch (error) {
                    console.error(error);
                    toast.error("Something went wrong while updating user");
                }
            }
            else {
                const createdUser = await createUser(token, data);

                if (createdUser.data?.data?.code === 11000) {
                    toast.error("Admin user already exists");
                } else if (createdUser.data?.message === "Error creating a user") {
                    toast.error("Error creating a user");
                } else {
                    toast.success(
                        isEditing
                            ? "Admin user updated successfully"
                            : "Admin user added successfully"
                    );

                }

            }
            fetchAdminUsers();

        } catch (error) {
            console.error(error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-container d-flex">
            <Sidebar />
            <div className="right-container flex-grow-1 p-3">
                <div className="flex justify-end">
                    <Button variant="primary" className="bg-primaryColor" onClick={handleShowModal}>
                        Create Admin
                    </Button>
                </div>
                <h1 className="fw-bold fs-4">Admin Users</h1>


                <GenericLoaderComponent isLoading={loading} />

                {/* Admin Users Table */}
                <div className="quotes-table mt-4">
                    <table className="table table-bordered table-hover align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>User Name</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Phone Number</th>
                                <th>Account Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {adminUsers?.length > 0 ? (
                                adminUsers.map((item, i) => (
                                    <tr key={i}>
                                        <td>{item.email}</td>
                                        <td>{item.firstName}</td>
                                        <td>{item.lastName}</td>
                                        <td>{item.phoneNumber}</td>
                                        <td>{item.accountStatus}</td>
                                        <td>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => {
                                                    const updatedUser = {
                                                        ...item,
                                                        email: item.email?.split("@")[0] || "",
                                                    };
                                                    handleEditAdminUser(updatedUser)

                                                }}
                                            >
                                                Edit/Deactivate
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-center py-3">
                                        No Admin Users Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <Modal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    size="lg"
                    centered
                    scrollable
                >
                    <Modal.Header closeButton>
                        <Modal.Title className="text-center font-semibold">
                            {isEditing ? "Edit Admin User" : "Create Admin User"}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <div className="admin-user-body">
                            <div className="row mb-3">
                                <div className="col">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="First name *"
                                        value={adminValue.firstName}
                                        onChange={(e) =>
                                            setAdminValue({
                                                ...adminValue,
                                                firstName: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                                <div className="col">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Surname *"
                                        value={adminValue.lastName}
                                        onChange={(e) =>
                                            setAdminValue({
                                                ...adminValue,
                                                lastName: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Primary email *"
                                        value={adminValue.email}
                                        onChange={(e) => {
                                            let value = e.target.value;

                                            if (value.includes("@")) {
                                                value = value.split("@")[0];
                                            }
                                            setAdminValue({
                                                ...adminValue,
                                                email: value.trim(),
                                            });
                                        }}
                                    />

                                    {/* ✅ Helper text under input */}
                                    <small className="form-text text-muted">
                                        Example: <strong>john.doe</strong> (we’ll automatically add @{companyDomain})
                                    </small>
                                </div>

                                <div className="col">
                                    <input
                                        type="text"
                                        className="form-control"
                                        disabled
                                        value={`@${companyDomain}`}
                                    />
                                </div>
                            </div>

                            <div className="mb-3 text-start">
                                <label className="form-label me-3">Admin Status:</label>
                                <input
                                    type="radio"
                                    name="status"
                                    value="Active"
                                    checked={adminValue.accountStatus === "Active"}
                                    onChange={(e) =>
                                        setAdminValue({
                                            ...adminValue,
                                            accountStatus: e.target.value,
                                        })
                                    }
                                />
                                &nbsp;Active&nbsp;
                                <input
                                    type="radio"
                                    name="status"
                                    value="Inactive"
                                    checked={adminValue.accountStatus === "Inactive"}
                                    onChange={(e) =>
                                        setAdminValue({
                                            ...adminValue,
                                            accountStatus: e.target.value,
                                        })
                                    }
                                />
                                &nbsp;Inactive
                            </div>

                            <h6 className="mt-4 mb-3">Email Subscriptions</h6>
                            <div className="row">
                                {[
                                    { label: "Quote Created", field: "adminQuoteCreatedEmailSub" },
                                    { label: "Issue Raised", field: "adminIssueRaisedEmailSub" },
                                    { label: "Review Target Cost", field: "adminReviewTargetCostEmailSub" },
                                    { label: "Order Placed", field: "adminOrderPlacedEmailSub" },
                                    // { label: "Stripe Event", field: "adminStripeEventEmailSub" },
                                    { label: "Feedback Received", field: "adminFeedbackReceivedEmailSub" },
                                    { label: "SO Report", field: "adminSoReportEmailSub" },
                                    { label: "RFQ", field: "adminRfqEmailSub" },
                                    { label: "Offer", field: "adminOfferEmailSub" },
                                    { label: "Accept PO", field: "adminAcceptPoEmailSub" },
                                    { label: "Bills Creation", field: "adminBillsCreationEmailSub" },
                                    { label: "ESD Change", field: "adminEsdChangeEmailSub" },
                                ].map((sub, i) => (
                                    <div className="col-6 col-md-4 mb-3" key={i}>
                                        <SubscriptionToggle
                                            label={sub.label}
                                            value={adminValue[sub.field]}
                                            fieldName={sub.field}
                                            updateEmailSubscription={updateEmailSubscription}
                                        />
                                    </div>
                                ))}
                            </div>

                            {error && (
                                <div className="text-danger text-center mt-2">{error}</div>
                            )}
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button className="bg-primaryColor" onClick={handleSaveAdminUser}>
                            {isEditing ? "Update Admin User" : "Add Admin User"}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div >
    );
};

export default AdminUsers;

const SubscriptionToggle = ({
    label,
    value,
    fieldName,
    updateEmailSubscription,
}) => (
    <label className="d-flex align-items-center gap-2">
        <span className="flex-grow-1">{label}</span>
        <Toggle
            checked={value}
            icons={false}
            onChange={(e) => updateEmailSubscription(e.target.checked, fieldName)}
        />
    </label>
);


function getInitialAdminValues() {
    return {
        firstName: "",
        lastName: "",
        email: "",
        userType: "admin",
        password: "",
        accountStatus: "",
        adminQuoteCreatedEmailSub: true,
        adminIssueRaisedEmailSub: true,
        adminReviewTargetCostEmailSub: true,
        adminOrderPlacedEmailSub: true,
        adminStripeEventEmailSub: true,
        adminFeedbackReceivedEmailSub: true,
        adminSoReportEmailSub: true,
        adminRfqEmailSub: true,
        adminOfferEmailSub: true,
        adminAcceptPoEmailSub: true,
        adminBillsCreationEmailSub: true,
        adminEsdChangeEmailSub: true,
    };
}